import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { config } from "~/config";
import {
  UploadCompoundRecordComposersFileDocument,
  type UploadCompoundRecordComposersFileMutation,
  type UploadCompoundRecordComposersFileMutationVariables,
} from "~/features/compoundRecordComposers/gqls";
import { toInsertedRecordFromSuccess } from "~/features/compoundRecordComposers/mappers";
import {
  COMPOUND_RECORD_COMPOSER_CANDIDATE_ID_TYPES,
  type CompoundRecordComposerCandidateIdType,
  type InsertedRecord,
} from "~/features/compoundRecordComposers/types-upload";
import type { CompoundRecordComposerCsvRow } from "~/features/compoundRecordComposers/utils";
import { parseCompoundRecordComposerCsv } from "~/features/compoundRecordComposers/utils";
import { useTenant } from "~/features/tenant/context";

interface UseCompoundRecordComposersUploadResult {
  csvData: CompoundRecordComposerCsvRow[];
  error: string | null;
  isLoading: boolean;
  isUploading: boolean;
  resetKey: number;
  insertedRecords: InsertedRecord[];
  jobRunId: string | null;
  maxRows: number;
  maxFileSizeBytes: number;
  candidateIdType: CompoundRecordComposerCandidateIdType;
  setCandidateIdType: (candidateIdType: CompoundRecordComposerCandidateIdType) => void;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleUpload: () => Promise<void>;
  handleReset: () => void;
}

export const useCompoundRecordComposersUpload = (): UseCompoundRecordComposersUploadResult => {
  const { t } = useTranslation();
  const [csvData, setCsvData] = useState<CompoundRecordComposerCsvRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [resetKey, setResetKey] = useState<number>(0);
  const [insertedRecords, setInsertedRecords] = useState<InsertedRecord[]>([]);
  const [jobRunId, setJobRunId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [candidateIdType, setCandidateIdType] = useState<CompoundRecordComposerCandidateIdType>(
    COMPOUND_RECORD_COMPOSER_CANDIDATE_ID_TYPES.RECORD_METADATA_ID,
  );
  const [uploadMutation] = useMutation<
    UploadCompoundRecordComposersFileMutation,
    UploadCompoundRecordComposersFileMutationVariables
  >(UploadCompoundRecordComposersFileDocument);
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  const maxRows = config.compoundRecordComposerFileUpload.maxRows;
  const maxFileSizeBytes = config.compoundRecordComposerFileUpload.maxFileSizeBytes;
  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsLoading(true);
      setError(null);

      try {
        if (file.size > maxFileSizeBytes) {
          throw new Error(
            t("feature.compoundRecordComposers.upload.fileTooLarge", {
              max: maxFileSizeBytes / 1_000_000,
            }),
          );
        }
        const content = await file.text();
        const data = parseCompoundRecordComposerCsv(content);
        setCsvData(data);
        setFile(file);
        setUploadId(crypto.randomUUID());
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to read file");
        setCsvData([]);
        setFile(null);
        setUploadId(null);
      } finally {
        setIsLoading(false);
      }
    },
    [maxFileSizeBytes, t],
  );

  const handleReset = useCallback(() => {
    setCsvData([]);
    setError(null);
    setInsertedRecords([]);
    setJobRunId(null);
    setFile(null);
    setUploadId(null);
    setResetKey((prev) => prev + 1);
  }, []);

  const handleCandidateIdTypeChange = useCallback((nextCandidateIdType: CompoundRecordComposerCandidateIdType) => {
    setCandidateIdType(nextCandidateIdType);
    setUploadId(crypto.randomUUID());
  }, []);

  const handleUpload = useCallback(async () => {
    if (csvData.length === 0 || csvData.length > maxRows || !file || !uploadId) return;

    setIsUploading(true);
    setError(null);

    try {
      const input = { tenantId, candidateIdType, file, uploadId };
      const result = await uploadMutation({
        variables: { input },
      });

      if (result.data?.cnutUploadCompoundRecordComposersFile.__typename === "ValidationError") {
        const validationError = result.data.cnutUploadCompoundRecordComposersFile;
        setError(validationError.message);
        toast.error(validationError.message);
        return;
      }

      if (result.data?.cnutUploadCompoundRecordComposersFile.__typename !== "CompoundRecordComposerFileUploadOutput") {
        return;
      }

      const output = result.data.cnutUploadCompoundRecordComposersFile;
      const isReplay = output.totalProcessed === 0 && output.jobRunId != null;
      setJobRunId(output.jobRunId ?? null);
      if (output.finalizeStatus === "failed") {
        const errorMessage = t("feature.compoundRecordComposers.upload.jobFailed");
        setError(errorMessage);
        toast.error(errorMessage);
      } else if (isReplay) {
        toast.success(t("feature.compoundRecordComposers.upload.uploadReplayConfirmed"));
      } else {
        toast.success(
          t("feature.compoundRecordComposers.upload.uploadSuccess", {
            count: output.totalProcessed,
          }),
        );
      }

      const records = output.successfulComposers.map((item) => toInsertedRecordFromSuccess(item, csvData));
      setInsertedRecords(records);

      setCsvData([]);
      setFile(null);
      setUploadId(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to upload CSV";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  }, [candidateIdType, csvData, file, maxRows, t, tenantId, uploadId, uploadMutation]);

  return {
    csvData,
    error,
    isLoading,
    isUploading,
    resetKey,
    insertedRecords,
    jobRunId,
    maxRows,
    maxFileSizeBytes,
    candidateIdType,
    setCandidateIdType: handleCandidateIdTypeChange,
    handleFileUpload,
    handleUpload,
    handleReset,
  };
};
